'use client'

import './Form.scss'

import cn from 'classnames'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { forwardRef, useEffect, useImperativeHandle } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

import { type IFormFeedback } from '@/src/entities/application/model'
import { IBookingTime } from '@/src/entities/event/api/interfaces'
import { defaultLocale } from '@/src/shared/config/i18n'
import { FieldCheckbox } from '@/src/shared/ui/FieldCheckbox'
import { FieldDateTime } from '@/src/shared/ui/FieldDateTime'
import { FieldSelect } from '@/src/shared/ui/FieldSelect'
import { FieldText } from '@/src/shared/ui/FieldText'

const makeOptions = <V extends string>(
  values: readonly V[],
  toLabel: (v: V) => string = (v) => v,
) => values.map((v) => ({ id: v, label: toLabel(v), value: v }))

export type IProps = {
  className?: string
  children: React.ReactNode
  errors: Record<string, string[]>
  landingId?: number | string | null
  locale?: string
  dates: IBookingTime
  onSubmit: (payload: IFormFeedback) => void
}

enum DEPARTMENT {
  MARKETING = 'Marketing',
  BUSINESS_DEVELOPMENT = 'Business Development',
}

type IOption<V extends string = string> = {
  id: V
  label: string
  value: V
}

type FormValues = Omit<IFormFeedback, 'department'> & {
  department: IOption<Department>
}

type Department = (typeof DEPARTMENT)[keyof typeof DEPARTMENT]

export type ImperativeHandlers = {
  resetForm: () => void
}

const initialValues: FormValues = {
  locale: defaultLocale,
  landing_id: '',
  name: '',
  email: '',
  text: '',
  phone: '',
  company: '',
  agree: [],
  email_subscription: [],
  meeting_time: '',
  department: {
    id: DEPARTMENT.BUSINESS_DEVELOPMENT,
    label: DEPARTMENT.BUSINESS_DEVELOPMENT,
    value: DEPARTMENT.BUSINESS_DEVELOPMENT,
  },
}
const departmentOptions = makeOptions(Object.values(DEPARTMENT) as readonly Department[])

const Form = forwardRef<ImperativeHandlers, IProps>(
  ({ className, children, onSubmit, errors = {}, landingId = '', locale, dates }, ref) => {
    const t = useTranslations('FormFields')

    const { control, handleSubmit, reset, setFocus } = useForm<FormValues>({
      defaultValues: initialValues,
    })

    useImperativeHandle(ref, () => ({
      resetForm() {
        reset(initialValues)
      },
    }))

    useEffect(() => {
      const firstError = Object.keys(errors).reduce<string>((field, a) => {
        return errors[field ?? ''] ? field : a
      }, '')

      if (firstError) {
        setFocus(firstError as keyof FormValues)
      }
    }, [errors, setFocus])

    const handler: SubmitHandler<FormValues> = (data) => {
      return onSubmit({
        ...data,
        landing_id: landingId as string,
        locale: locale as string,
        department: data.department.value as Department,
      })
    }

    return (
      <form
        action={handleSubmit(handler) as unknown as (data: FormData) => void}
        className={cn('form', {
          [`${className}`]: !!className,
        })}
      >
        <div className="form__box">
          <div className="form__row">
            <div className="form__cell">
              <Controller
                name="name"
                control={control}
                render={({ field }) => <FieldText placeholder={t('fieldName')} {...field} />}
              />
            </div>

            <div className="form__cell">
              <Controller
                name="company"
                control={control}
                render={({ field }) => <FieldText placeholder={t('fieldCompany')} {...field} />}
              />
            </div>
          </div>

          <div className="form__row">
            <div className="form__cell">
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <FieldText
                    errors={errors['email']}
                    placeholder={`${t('fieldEmail')} *`}
                    type="email"
                    {...field}
                  />
                )}
              />
            </div>
            <div className="form__cell">
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <FieldText
                    errors={errors['phone']}
                    placeholder={`${t('fieldPhone')} *`}
                    type="tel"
                    {...field}
                  />
                )}
              />
            </div>
          </div>
          <div className="form__row form__row--department">
            <div className="form__cell">
              <Controller
                name="department"
                control={control}
                render={({ field }) => (
                  <FieldSelect
                    id="department"
                    placeholder="Department"
                    value={field.value}
                    onChange={(val) => field.onChange(val as IOption)}
                    options={departmentOptions}
                  />
                )}
              />
            </div>
          </div>
          <div className="form__row">
            <div className="form__cell form__cell--full-width">
              <Controller
                name="meeting_time"
                control={control}
                render={({ field }) => (
                  <FieldDateTime
                    placeholderDate={t('fieldPreferredDate')}
                    placeholderTime={t('fieldPreferredTime')}
                    dates={dates}
                    {...field}
                  />
                )}
              />
            </div>
          </div>

          <div className="form__row">
            <div className="form__cell form__cell--full-width">
              <Controller
                name="text"
                control={control}
                render={({ field }) => (
                  <FieldText placeholder={t('fieldMsg')} type="textarea" {...field} />
                )}
              />
            </div>
          </div>

          <div className="form__row">
            <div className="form__cell form__cell--full-width">
              <Controller
                name="agree"
                control={control}
                render={({ field }) => (
                  <FieldCheckbox
                    errors={errors['agree']}
                    {...field}
                    id="true"
                    value={field.value as (string | number)[]}
                  >
                    {t('fieldAgree')}&nbsp;
                    <Link
                      href="/docs/privacy-policy.pdf"
                      target="_blank"
                      rel="noreferrer noopener"
                      className="form__cell_link"
                    >
                      {t('personalData')}
                    </Link>
                  </FieldCheckbox>
                )}
              />
            </div>
          </div>

          <div className="form__row">
            <div className="form__cell form__cell--full-width">
              <Controller
                name="email_subscription"
                control={control}
                render={({ field }) => (
                  <FieldCheckbox {...field} id="true" value={field.value as (string | number)[]}>
                    {t('fieldSubscription')}
                  </FieldCheckbox>
                )}
              />
            </div>
          </div>
        </div>

        <div className="form__control">{children}</div>
      </form>
    )
  },
)

Form.displayName = 'Form'

export default Form
